import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, finalize, take, takeUntil } from 'rxjs';
import { JourneyCoreService } from '../../core/journey-core/journey-core.service';
import {
  GenieGames,
  IJourney,
  IJumble,
  IQuestion,
  IQuestionOptions,

  QuizQuestion,
  CortevaParticipant
} from '../../core/journey-core/journey.interface';
import { MusicLevelsEnum, CorrectAnswerType } from 'src/app/common/interfaces';
import { AuthCoreService } from 'src/app/core/auth-core/auth-core.service';
import {
  NotificationService,
  NotificationType,
} from 'src/app/notification/notification/notification.service';
import { IStep } from 'src/app/shared/stepper/step.interface';
import { StepperService } from 'src/app/shared/stepper/stepper.service';
import { Router } from '@angular/router';

@Injectable()
export class JourneyService implements OnDestroy {
  private _unsubscribe$ = new Subject<void>();
  journeyList$ = this._journeyCoreService.journeyList$;
  generatingPackagesFor: string[] = [];

  steps: IStep[] = [
    {
      step: 'Name',
      disabled: false,
      completed: false,
      loadingNext: false,
    },
    {
      step: 'Game Format',
      disabled: true,
      completed: false,
      loadingNext: false,
    },
    {
      step: 'Topic',
      disabled: true,
      completed: false,
      loadingNext: false,
    },
    {
      step: 'Content',
      disabled: true,
      completed: false,
      loadingNext: false,
    },
    {
      step: 'Questions',
      disabled: true,
      completed: false,
      loadingNext: false,
    },
    {
      step: 'Miscellaneous',
      disabled: true,
      completed: false,
      loadingNext: false,
    },
    {
      step: 'Learning content',
      disabled: true,
      completed: false,
      loadingNext: false,
    },
  ];

  languages: Array<
    'hi' | 'mr' | 'gu' | 'ml' | 'en' | 'pa' | 'bn' | 'as' | 'or' | 'ta' | 'te' | 'kn'
  > = [];

  languageMap = {
    hi: 'Hindi',
    mr: 'Marathi',
    gu: 'Gujarati',
    ml: 'Malayalam',
    en: 'English',
    pa: 'Punjabi',
    bn: 'Bengali',
    or: 'Odia',
    as: 'Assamese',
    ta: 'Tamil',
    te: 'Telugu',
    kn: 'Kannada',
  } as any;

  genieGameNames = {
    AngryBirdGame: 'Angry Birds Game',
    MarioGame: 'Mario Game',
    Kbc: 'KBC Game',
    SliderGame: 'Slider Game',
    BubbleShooterGame: 'Bubble Shooter Game',
    QuestionsGame: 'Quiz Game',
    FirePilotGame: 'Fire Pilot Game',
    StickCricketGame: 'Stick Cricket Game',
    FruitNinjaGame: 'Fruit Ninja Game',
    SwipeGame: 'Swipe Game',
    SwipeGame2: 'Swipe Game 2',
    JumblesGame: 'Jumbles Game',
    CrosswordsGame: 'Crosswords Game',
    WordsPuzzleGame: 'Words Puzzle Game',
    MazeGame: 'Maze Game',
    PoolGame: 'Pool Game',
    RacingGame: 'Racing Game',
    SpotOnGame: 'SpotOnGame',
    SnakeLadderGame: 'Snake Ladder Game',
    ParadropGame: 'Para Drop Game',
    StraightShooterGame: 'Straight Shooter Game',
  };

  genieGameFormatName = {
    AngryBirdGame: 'Angry Birds',
    MarioGame: 'Mario',
    Kbc: 'Hot seat',
    SliderGame: 'Slider Game',
    BubbleShooterGame: 'Bubble Shooter',
    QuestionsGame: 'Quiz',
    FirePilotGame: 'Fire Pilot',
    StickCricketGame: 'Stick Cricket',
    FruitNinjaGame: 'Slice Master',
    SwipeGame: 'Swipe true or false',
    SwipeGame2: "It's a match",
    JumblesGame: 'Jumbles',
    CrosswordsGame: 'Crosswords',
    WordsPuzzleGame: 'Words Puzzle',
    MazeGame: 'Puzzle Paths',
    PoolGame: 'Brainy Billiards',
    RacingGame: 'Racing Game',
    PathFinderGame: 'Path Finder Game',
    SpotOnGame: 'SpotOnGame',
    SnakeLadderGame: 'Snake Ladder Game',
    ParadropGame: 'Para Drop Game',
    StraightShooterGame: 'Straight Shooter Game',
  };

  gameOptionNumberMap: GameOptionNumberMap = {
    AngryBirdGame: 3,
    MarioGame: 4,
    Kbc: 4,
    SliderGame: 4,
    BubbleShooterGame: 3,
    QuestionsGame: 4,
    FirePilotGame: 4,
    StickCricketGame: 4,
    FruitNinjaGame: 4,
    SwipeGame: 2,
    SwipeGame2: 3,
    JumblesGame: 1,
    CrosswordsGame: 1,
    MazeGame: 4,
    PoolGame: 4,
    SpotOnGame: 4,
    RacingGame: 4,
    PathFinderGame: 3,
    SnakeLadderGame: 4,
    ParadropGame: 4,
    StraightShooterGame: 4,
    WordsPuzzleGame:1,
  };

  constructor(
    private _journeyCoreService: JourneyCoreService,
    private _notificationService: NotificationService,
    private _router: Router,
    private _stepperService: StepperService,
  ) {}

  getJourneys(): void {
    this._journeyCoreService.getJourneys();
  }

  getJourneyLanguages(format: GenieGames) {
    return this._journeyCoreService.getJourneyLanguages(format);
  }

  addJourney(data: Partial<IJourney>): Observable<IJourney> {
    delete data._id;
    return this._journeyCoreService.addJourney(data);
  }

  updateJourney(id: string, data: Partial<IJourney>): Observable<IJourney> {
    return this._journeyCoreService.updateJourney(id, data);
  }



  getCortevaParticipants(journeyId: string): Observable<CortevaParticipant[]> {
    return this._journeyCoreService.getCortevaParticipants(journeyId);
  }

  uploadCortevaParticipants(journeyId: string, file: File): Observable<any> {
    return this._journeyCoreService.uploadCortevaParticipants(journeyId, file);
  }

  upsertQuestions(
    id: string,
    data: QuizQuestion[] | QuizQuestion,
    excelUpload = false,
    index: number,
  ): Observable<IJourney> {
    return this._journeyCoreService.upsertQuestions(
      id,
      excelUpload ? data : [data as QuizQuestion],
      excelUpload,
      index,
    );
  }

  updateQuestion(
    id: string,
    questionId: string,
    question: QuizQuestion,
  ): Observable<IJourney> {
    return this._journeyCoreService.updateQuestion(id, questionId, question);
  }

  upsertJumbles(payload: {
    id: string;
    jumblesData: IJumblePayload[];
    excelUpload: boolean;
    index?: number;
    gameFormat?: GenieGames;
  }): Observable<IJourney> {
    return this._journeyCoreService.upsertJumbles(payload);
  }

  updateJumbles(payload: IJumbleUpdatePayload): Observable<IJourney> {
    return this._journeyCoreService.updateJumbles(payload);
  }

  deleteJourney(data: any): Observable<any> {
    return this._journeyCoreService.deleteJourney(data);
  }

  deleteQuestion(id: string, data: IQuestion): Observable<IJourney> {
    return this._journeyCoreService.deleteQuestion(id, data);
  }

  deleteJumble(
    id: string,
    data: IJumble,
    gameFormat?: GenieGames,
  ): Observable<IJourney> {
    return this._journeyCoreService.deleteJumble(id, data, gameFormat);
  }

  reorderQuestions(
    journeyId: string,
    questions: string[],
  ): Observable<IJourney> {
    return this._journeyCoreService.reorderQuestions(journeyId, questions);
  }

  reorderJumbles(
    journeyId: string,
    jumblesIds: string[],
    gameFormat?: GenieGames,
  ): Observable<IJourney> {
    return this._journeyCoreService.reorderJumbles(
      journeyId,
      jumblesIds,
      gameFormat,
    );
  }

  generateTextContent(id: string, topic: string): Observable<any> {
    return this._journeyCoreService.generateTextContent(id, topic);
  }

  uploadFile(payload: {
    type: 'pdf' | 'video';
    file: FormData;
    journeyId: string;
    language?: string;
  }): Observable<any> {
    return this._journeyCoreService.uploadFile(payload);
  }

  uploadVideo(payload: { file: File; journeyId: string }): Observable<any> {
    return this._journeyCoreService.uploadVideo(payload);
  }

  deleteTutorialFile(payload: {
    type: 'pdf' | 'video';
    journeyId: string;
    name: string;
    url: string;
    _id: string;
    fileId?: string;
  }): Observable<any> {
    return this._journeyCoreService.deleteTutorialFile(payload);
  }

  uploadTutorialFile(payload: {
    gameType: 'Pdf' | 'Videos';
    gameFormat: GenieGames;
    fileId: string;
    learningGameId: string;
  }): Observable<any> {
    return this._journeyCoreService.uploadTutorialFile(payload);
  }

  generatingPackage(id: string, selfContained?: boolean): void {
    this.generatingPackagesFor.push(id);
    this._journeyCoreService
      .generatePackage(id, selfContained)
      .pipe(
        take(1),
        takeUntil(this._unsubscribe$),
        finalize(() => {
          const index = this.generatingPackagesFor.indexOf(id);
          if (index !== -1) {
            this.generatingPackagesFor.splice(index, 1);
          }
        }),
      )
      .subscribe({
        next: (journey: IJourney) => {
          window.open(journey.packageUrl, '_blank');
          this._notificationService.showNotification(
            'Package generated successfully',
            NotificationType.INFO,
          );
        },
        error: (err) => {
          console.error('Error: ', err?.error?.message);
          this._notificationService.showNotification(
            err?.error?.message,
            NotificationType.ERROR,
          );
        },
      });
  }

  generateQuestionsFromTextContent(
    id: string,
    textContent: string,
    topic: string,
    gameFormat: GenieGames,
    isJumbles: boolean,
  ): Observable<IJourney> {
    return this._journeyCoreService.generateQuestions(
      id,
      textContent,
      topic,
      gameFormat,
      isJumbles,
    );
  }

  generateSingleQuestion(payload: {
    journeyId: string;
    index: number;
    topic: string;
    gameFormat: string;
    questions: string[];
    isContentFile: boolean;
    count: number;
    abortSignal: AbortSignal;
  }): Observable<IJourney> {
    return this._journeyCoreService.generateSingleQuestion(payload);
  }

  generateSingleJumble(payload: {
    journeyId: string;
    index: number;
    topic: string;
    jumbles: string[];
    isContentFile: boolean;
    count: number;
    gameFormat?: GenieGames;
  }): Observable<IJourney> {
    return this._journeyCoreService.generateSingleJumble(payload);
  }

  generateQuestionsFromContentFiles(
    id: string,
    isJumbles: boolean,
    gameFormat?: GenieGames,
  ): Observable<IJourney> {
    return this._journeyCoreService.generateQuestionsFromContentFiles(
      id,
      isJumbles,
      gameFormat,
    );
  }

  navigateToEditPage(journeyId: string, landingStep: number): void {
    const urlFragments = this._router.url.split('/');
    urlFragments[urlFragments.length - 1] = `edit/${journeyId}`;
    const newUrl = urlFragments.join('/');
    // const editStepperId = `/edit/${data._id}`;
    const initiEditSteps = [...this.steps];
    initiEditSteps[landingStep - 1].disabled = false;
    initiEditSteps[landingStep - 1].completed = true;
    initiEditSteps[landingStep].disabled = false;
    console.log('newURl: ', newUrl);
    this._stepperService.setSteps(newUrl, initiEditSteps);
    this._stepperService.changeStep(newUrl, landingStep);
    this._router.navigateByUrl(newUrl);
  }

  syncQuestions(id: string, questions: IQuestion[]): Observable<IJourney> {
    return this._journeyCoreService.syncQuestions(id, questions);
  }

  getJourney(id: string): Observable<any> {
    return this._journeyCoreService.getJourney(id);
  }

  public refresh(): void {
    this._journeyCoreService.refresh(true);
  }

  parseOption(formOption: IFormOption): IQuestionOptions {
    return {
      _id: formOption._id || undefined,
      text: formOption.text,
      sequenceId: {
        _id: formOption.sequenceId || undefined,
        data: [
          {
            text: formOption.feedback,
          },
        ],
        companyId: AuthCoreService.companyUser.company._id,
      },
      isCorrect: formOption.isCorrect,
    };
  }

  parseQuestion(
    question: {
      text: string;
      options: IQuestionOptions[];
    },
    name: string,
    index: number,
  ): QuizQuestion {
    let options = {} as Record<string, any>;
    const feedback = {} as Record<string, any>;
    let correctOptionKey = '';

    question.options.forEach((item, index) => {
      const optionKey = String.fromCharCode(97 + index);

      options[optionKey] = item.text;

      feedback[optionKey] = item.feedback;

      if (item.isCorrect) {
        correctOptionKey = optionKey;
      }
    });
    return {
      index,
      questionText: question.text,
      options,
      feedback,
      correct_option: correctOptionKey,
    };
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }
}

export type GameOptionNumberMap = {
  AngryBirdGame: number;
  MarioGame: number;
  Kbc: number;
  SliderGame: number;
  BubbleShooterGame: number;
  QuestionsGame: number;
  FirePilotGame: number;
  StickCricketGame: number;
  FruitNinjaGame: number;
  SwipeGame: number;
  JumblesGame: number;
  CrosswordsGame: number;
  WordsPuzzleGame: number;
  SwipeGame2: number;
  MazeGame: number;
  PoolGame: number;
  RacingGame: number;
  PathFinderGame: number;
  SpotOnGame: number;
  SnakeLadderGame: number;
  ParadropGame: number;
  StraightShooterGame: number;
};

export interface IFormOption {
  _id?: string;
  text: string;
  isCorrect: boolean;
  feedback: string;
  sequenceId: string;
}

export interface IJumblePayload {
  word: string;
  hint: string;
}

export interface IJumbleUpdatePayload {
  _id: string;
  hint: string;
  correctWord: string;
  gameFormat?: GenieGames;
}
